import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentListIcon, ChevronLeftIcon, ArrowPathIcon,
  MagnifyingGlassIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const fmtD  = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}).replace(/ /g,'-') : '—';
const fmtDT = d => d ? new Date(d).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';
const ini   = (n='') => n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'S';

const STATUS_COLORS = {
  draft:        { bg:'#f0f2f7', color:'#6b7194' },
  active:       { bg:'#ecfdf5', color:'#10c98e' },
  under_review: { bg:'#fff7ed', color:'#f97316' },
  completed:    { bg:'#eef2ff', color:'#3b6ef8' },
  discontinued: { bg:'#fef2f2', color:'#ef4444' },
};
const RATING_COLORS = {
  excellent:        { bg:'#ecfdf5', color:'#10c98e' },
  good:             { bg:'#eef2ff', color:'#3b6ef8' },
  satisfactory:     { bg:'#f3f0ff', color:'#8b5cf6' },
  needs_improvement:{ bg:'#fff7ed', color:'#f97316' },
  concern:          { bg:'#fef2f2', color:'#ef4444' },
};
const GOAL_STATUS_COLORS = {
  not_started:  { bg:'#f0f2f7', color:'#6b7194' },
  in_progress:  { bg:'#fff7ed', color:'#f97316' },
  achieved:     { bg:'#ecfdf5', color:'#10c98e' },
  discontinued: { bg:'#fef2f2', color:'#ef4444' },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ai*{box-sizing:border-box;} .ai{font-family:'Sora',sans-serif;color:#1a1d2e;animation:aiFade .4s ease both;}
  @keyframes aiFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .ai-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.5rem;flex-wrap:wrap;gap:12px;}
  .ai-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .ai-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .btn{padding:8px 16px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .btn svg{width:15px;height:15px;stroke-width:2;flex-shrink:0;}
  .btn-blue{background:#3b6ef8;color:white;} .btn-blue:hover{box-shadow:0 4px 14px rgba(59,110,248,.35);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-amber{background:#fffbeb;color:#d97706;border:1px solid #fde68a;} .btn-amber:hover{background:#d97706;color:white;}
  .btn-sm{padding:5px 12px;font-size:.74rem;} .btn:disabled{opacity:.5;cursor:not-allowed;}
  .stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:20px;}
  .stat{background:white;border-radius:12px;border:1px solid #e8ebf4;padding:14px 16px;}
  .stat-val{font-size:1.5rem;font-weight:700;color:#0f1123;} .stat-lbl{font-size:.72rem;color:#9094a8;margin-top:2px;}
  .filters{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;}
  .search-wrap{position:relative;} .search-wrap svg{position:absolute;left:10px;top:50%;transform:translateY(-50%);width:14px;color:#9094a8;stroke-width:2;}
  .search-input{font-family:'Sora',sans-serif;font-size:.8rem;padding:8px 12px 8px 32px;border:1.5px solid #e0e4f0;border-radius:9px;outline:none;background:#f8f9fc;color:#1a1d2e;width:220px;}
  .search-input:focus{border-color:#3b6ef8;background:white;}
  .fsel{font-family:'Sora',sans-serif;font-size:.8rem;padding:8px 12px;border:1.5px solid #e0e4f0;border-radius:9px;outline:none;background:#f8f9fc;color:#1a1d2e;}
  .fsel:focus{border-color:#3b6ef8;background:white;}
  .card{background:white;border-radius:14px;border:1px solid #e8ebf4;padding:16px 18px;margin-bottom:10px;cursor:pointer;transition:.15s;}
  .card:hover{border-color:#3b6ef8;box-shadow:0 2px 12px rgba(59,110,248,.08);}
  .pill{display:inline-flex;font-size:.67rem;font-weight:700;padding:3px 10px;border-radius:99px;}
  .av{width:34px;height:34px;border-radius:9px;background:linear-gradient(135deg,#3b6ef8,#8b5cf6);display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;color:white;flex-shrink:0;}
  .progress-bar-wrap{height:5px;background:#f0f2f7;border-radius:99px;overflow:hidden;margin-top:8px;}
  .progress-bar{height:100%;background:linear-gradient(90deg,#3b6ef8,#8b5cf6);border-radius:99px;}
  .empty{text-align:center;padding:52px;color:#b0b5cc;font-size:.82rem;}
  .spin{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:#3b6ef8;border-radius:50%;animation:sp .7s linear infinite;display:inline-block;}
  @keyframes sp{to{transform:rotate(360deg)}}
  .detail{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;}
  .detail-hdr{padding:20px 24px;border-bottom:1px solid #f0f2f8;display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px;}
  .detail-tabs{display:flex;gap:4px;padding:12px 16px;background:#f8f9fc;border-bottom:1px solid #f0f2f8;overflow-x:auto;}
  .dtab{padding:7px 16px;border-radius:9px;border:none;font-family:'Sora',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;background:transparent;color:#9094a8;white-space:nowrap;transition:.15s;}
  .dtab.on{background:white;color:#0f1123;box-shadow:0 1px 4px rgba(0,0,0,.1);}
  .detail-body{padding:24px;}
  .section-title{font-size:.72rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;margin-bottom:12px;}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:20px;}
  .info-item{display:flex;flex-direction:column;gap:4px;}
  .info-lbl{font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .info-val{font-size:.82rem;color:#1a1d2e;font-weight:500;}
  .goal-card{background:#f8f9fc;border-radius:12px;border:1px solid #e8ebf4;padding:14px;margin-bottom:10px;}
  .update-card{border-left:3px solid #3b6ef8;padding:12px 16px;margin-bottom:12px;background:#fafbff;border-radius:0 10px 10px 0;}
  .comment-card{background:#f8f9fc;border-radius:12px;padding:12px 14px;margin-bottom:10px;border:1px solid #e8ebf4;}
  .reply-box{background:#eef2ff;border-radius:8px;padding:8px 12px;margin-top:8px;font-size:.76rem;color:#3b6ef8;}
  .fi,.fta{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;transition:.15s;}
  .fi:focus,.fta:focus{border-color:#3b6ef8;background:white;} .fta{resize:vertical;min-height:72px;}
  .fl{font-size:.7rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .ff{display:flex;flex-direction:column;gap:5px;}
  .overlay{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;}
  .modal{background:white;border-radius:20px;width:100%;max-width:480px;max-height:85vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column;animation:mIn .25s ease;}
  @keyframes mIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
  .m-hdr{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;gap:8px;}
  .m-hdr-l{display:flex;align-items:center;gap:10px;}
  .m-icon{width:34px;height:34px;border-radius:10px;background:#eef2ff;color:#3b6ef8;display:flex;align-items:center;justify-content:center;}
  .m-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .m-title{font-size:.95rem;font-weight:700;color:#0f1123;} .m-sub{font-size:.72rem;color:#9094a8;}
  .m-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;}
  .m-close svg{width:14px;height:14px;stroke-width:2;}
  .m-body{padding:22px;overflow-y:auto;flex:1;}
  .m-foot{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;}
  @media(max-width:768px){.ai-title{font-size:1.2rem;}.info-grid{grid-template-columns:1fr;}}
`;

export default function AdminILP() {
  const [view,      setView]      = useState('list');
  const [ilps,      setIlps]      = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [detailTab, setDetailTab] = useState('overview');
  const [filter,    setFilter]    = useState({ status:'', search:'', academicYear:'' });
  const [modal,     setModal]     = useState(null);
  const [statusForm,setStatusForm]= useState({ status:'', closureNote:'' });
  const [progForm,  setProgForm]  = useState({ note:'', overallRating:'' });
  const [saving,    setSaving]    = useState(false);

  useEffect(() => { loadIlps(); }, [filter.status, filter.academicYear]);

  const loadIlps = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.status)       q.set('status',       filter.status);
      if (filter.academicYear) q.set('academicYear',  filter.academicYear);
      const { data } = await api.get(`/admin/ilp?${q}`);
      setIlps(data||[]);
    } catch { toast.error('Failed to load ILPs'); }
    finally { setLoading(false); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/ilp/${id}`);
      setSelected(data); setView('detail'); setDetailTab('overview');
    } catch { toast.error('Failed to load ILP'); }
  };

  const submitStatus = async () => {
    if (['completed','discontinued'].includes(statusForm.status) && !statusForm.closureNote.trim()) {
      toast.error('Closure reason is required'); return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/ilp/${selected._id}/status`, statusForm);
      toast.success('Status updated');
      setModal(null); openDetail(selected._id); loadIlps();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const submitProgress = async () => {
    if (!progForm.note.trim()) return toast.error('Note required');
    setSaving(true);
    try {
      await api.post(`/admin/ilp/${selected._id}/progress`, progForm);
      toast.success('Progress update added');
      setModal(null); setProgForm({ note:'', overallRating:'' }); openDetail(selected._id);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const filtered = ilps.filter(i => {
    if (filter.search) { const q=filter.search.toLowerCase(); return i.student?.user?.name?.toLowerCase().includes(q)||i.student?.admissionNo?.toLowerCase().includes(q); }
    return true;
  });

  const stats = {
    total:    ilps.length,
    active:   ilps.filter(i=>i.status==='active').length,
    review:   ilps.filter(i=>i.status==='under_review').length,
    completed:ilps.filter(i=>i.status==='completed').length,
  };

  const goalsDone = g => (g||[]).filter(x=>x.status==='achieved').length;

  return (
    <>
      <style>{css}</style>
      <div className="ai">

        {view==='list'&&(
          <>
            <div className="ai-hdr">
              <div><div className="ai-title">Individual Learning Plans</div><div className="ai-sub">Overview of all ILPs across the school.</div></div>
            </div>

            <div className="stats">
              <div className="stat"><div className="stat-val">{stats.total}</div><div className="stat-lbl">Total ILPs</div></div>
              <div className="stat"><div className="stat-val" style={{color:'#10c98e'}}>{stats.active}</div><div className="stat-lbl">Active</div></div>
              <div className="stat"><div className="stat-val" style={{color:'#f97316'}}>{stats.review}</div><div className="stat-lbl">Under Review</div></div>
              <div className="stat"><div className="stat-val" style={{color:'#3b6ef8'}}>{stats.completed}</div><div className="stat-lbl">Completed</div></div>
            </div>

            <div className="filters">
              <div className="search-wrap"><MagnifyingGlassIcon/><input className="search-input" placeholder="Search student…" value={filter.search} onChange={e=>setFilter(p=>({...p,search:e.target.value}))}/></div>
              <select className="fsel" value={filter.status} onChange={e=>setFilter(p=>({...p,status:e.target.value}))}>
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="discontinued">Discontinued</option>
                <option value="draft">Draft</option>
                <option value="under_review">Under Review</option>
              </select>
              <input className="fsel" placeholder="Year e.g. 2025-2026" value={filter.academicYear} onChange={e=>setFilter(p=>({...p,academicYear:e.target.value}))} style={{width:160}}/>
            </div>

            {loading?<div style={{textAlign:'center',padding:'48px',color:'#9094a8',fontSize:'.82rem'}}><span className="spin"/> Loading…</div>
            :!filtered.length?<div className="empty">No ILPs found.</div>
            :filtered.map(i=>{
              const sc=STATUS_COLORS[i.status]||STATUS_COLORS.draft;
              const done=goalsDone(i.goals); const total=i.goals?.length||0; const pct=total?Math.round(done/total*100):0;
              const name=i.student?.user?.name||'—';
              return (
                <div key={i._id} className="card" onClick={()=>openDetail(i._id)}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,marginBottom:8}}>
                    <div style={{display:'flex',gap:10,alignItems:'flex-start'}}>
                      <div className="av">{ini(name)}</div>
                      <div>
                        <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{name}</div>
                        <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:2}}>{i.student?.class?.name} · {i.student?.section} · {i.academicYear}</div>
                        <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:1}}>Teacher: {i.createdBy?.name||'—'}</div>
                      </div>
                    </div>
                    <span className="pill" style={{background:sc.bg,color:sc.color,flexShrink:0}}>{i.status?.replace('_',' ')}</span>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:'.72rem',color:'#9094a8'}}>
                    <span>Goals {done}/{total} · {i.progressUpdates?.length||0} updates</span>
                    <span>{i.parentAcknowledged?'✓ Parent acknowledged':'Pending acknowledgement'}</span>
                  </div>
                  {total>0&&<div className="progress-bar-wrap"><div className="progress-bar" style={{width:pct+'%'}}/></div>}
                </div>
              );
            })}
          </>
        )}

        {view==='detail'&&selected&&(
          <>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setView('list');setSelected(null);}}><ChevronLeftIcon/>Back</button>
              <div style={{flex:1}}/>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setProgForm({note:'',overallRating:''});setModal('progress');}}><ArrowPathIcon/>Add Progress</button>
              <button className="btn btn-amber btn-sm" onClick={()=>{setStatusForm({status:selected.status,closureNote:selected.closureNote||''});setModal('status');}}>Change Status</button>
            </div>

            <div className="detail">
              <div className="detail-hdr">
                <div style={{display:'flex',gap:14,alignItems:'flex-start'}}>
                  <div className="av" style={{width:44,height:44,fontSize:'.8rem'}}>{ini(selected.student?.user?.name||'')}</div>
                  <div>
                    <div style={{fontWeight:700,fontSize:'1.05rem',color:'#0f1123'}}>{selected.student?.user?.name}</div>
                    <div style={{fontSize:'.76rem',color:'#9094a8',marginTop:2}}>{selected.student?.class?.name} · Section {selected.student?.section} · {selected.student?.admissionNo}</div>
                    <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
                      <span className="pill" style={{...(STATUS_COLORS[selected.status]||STATUS_COLORS.draft)}}>{selected.status?.replace('_',' ')}</span>
                      <span className="pill" style={{background:'#f3f0ff',color:'#8b5cf6',textTransform:'capitalize'}}>{(selected.category||'').replace('_',' ')}</span>
                      <span className="pill" style={{background:'#f0f2f7',color:'#6b7194'}}>{selected.academicYear}</span>
                    </div>
                  </div>
                </div>
                <div style={{textAlign:'right',fontSize:'.72rem',color:'#9094a8'}}>
                  <div>Teacher: {selected.createdBy?.name}</div>
                  <div style={{marginTop:3}}>Updated {fmtD(selected.updatedAt)}</div>
                  {selected.parentAcknowledged&&<div style={{marginTop:3,color:'#10c98e',fontWeight:600}}>✓ Parent acknowledged</div>}
                </div>
              </div>

              <div style={{padding:'12px 24px',background:'#f8f9fc',borderBottom:'1px solid #f0f2f8',display:'flex',alignItems:'center',gap:14}}>
                <div style={{fontSize:'.76rem',color:'#6b7194',fontWeight:600,flexShrink:0}}>Goals: {goalsDone(selected.goals)}/{selected.goals?.length||0} achieved</div>
                <div className="progress-bar-wrap" style={{flex:1,margin:0}}><div className="progress-bar" style={{width:(selected.goals?.length?Math.round(goalsDone(selected.goals)/selected.goals.length*100):0)+'%'}}/></div>
              </div>

              <div className="detail-tabs">
                {['overview','goals','progress','comments'].map(t=>(
                  <button key={t} className={`dtab${detailTab===t?' on':''}`} onClick={()=>setDetailTab(t)} style={{textTransform:'capitalize'}}>{t}</button>
                ))}
              </div>

              <div className="detail-body">
                {detailTab==='overview'&&(
                  <div>
                    <div className="section-title">Plan Details</div>
                    <div className="info-grid">
                      <div className="info-item"><div className="info-lbl">Start Date</div><div className="info-val">{fmtD(selected.startDate)}</div></div>
                      <div className="info-item"><div className="info-lbl">End Date</div><div className="info-val">{fmtD(selected.endDate)}</div></div>
                      <div className="info-item"><div className="info-lbl">Review Frequency</div><div className="info-val" style={{textTransform:'capitalize'}}>{selected.reviewFrequency}</div></div>
                      <div className="info-item"><div className="info-lbl">Next Review</div><div className="info-val">{fmtD(selected.nextReviewDate)}</div></div>
                    </div>
                    <div className="section-title">Current Level</div>
                    {['strengths','weaknesses','learningGaps','learningStyle','assessmentBasis'].map(f=>selected.currentLevel?.[f]&&(
                      <div key={f} style={{marginBottom:12}}>
                        <div className="info-lbl" style={{marginBottom:4,textTransform:'capitalize'}}>{f.replace(/([A-Z])/g,' $1')}</div>
                        <div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4'}}>{selected.currentLevel[f]}</div>
                      </div>
                    ))}
                    {selected.supportStrategies&&<><div className="section-title" style={{marginTop:8}}>Support Strategies</div><div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4',marginBottom:12}}>{selected.supportStrategies}</div></>}
                    {selected.accommodations&&<><div className="section-title">Accommodations</div><div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4',marginBottom:12}}>{selected.accommodations}</div></>}
                  </div>
                )}
                {detailTab==='goals'&&(
                  <div>
                    <div className="section-title">Goals ({selected.goals?.length||0})</div>
                    {(selected.goals||[]).map((g,idx)=>{
                      const gs=GOAL_STATUS_COLORS[g.status]||GOAL_STATUS_COLORS.not_started;
                      return <div key={idx} className="goal-card">
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:6}}>
                          <div style={{fontSize:'.67rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase'}}>{g.area||'General'} · {g.type?.replace('_',' ')}</div>
                          <span className="pill" style={{...gs,textTransform:'capitalize',flexShrink:0}}>{g.status?.replace('_',' ')}</span>
                        </div>
                        <div style={{fontWeight:600,fontSize:'.83rem',color:'#1a1d2e',marginBottom:6}}>{g.description}</div>
                        {g.strategy&&<div style={{fontSize:'.76rem',color:'#6b7194'}}><strong>Strategy:</strong> {g.strategy}</div>}
                        {g.targetDate&&<div style={{fontSize:'.72rem',color:'#9094a8',marginTop:4}}>Target: {fmtD(g.targetDate)}</div>}
                      </div>;
                    })}
                  </div>
                )}
                {detailTab==='progress'&&(
                  <div>
                    <div className="section-title">Progress Updates ({selected.progressUpdates?.length||0})</div>
                    {![...(selected.progressUpdates||[])].length&&<div style={{color:'#b0b5cc',fontSize:'.82rem'}}>No updates yet.</div>}
                    {[...(selected.progressUpdates||[])].reverse().map((u,idx)=>{
                      const rc=RATING_COLORS[u.overallRating];
                      return <div key={idx} className="update-card">
                        <div style={{fontSize:'.7rem',color:'#9094a8',marginBottom:6}}><strong>{u.updatedBy?.name}</strong> ({u.role}) · {fmtDT(u.date||u.createdAt)}{rc&&<span className="pill" style={{...rc,marginLeft:8,textTransform:'capitalize'}}>{u.overallRating?.replace('_',' ')}</span>}</div>
                        <div style={{fontSize:'.82rem',color:'#1a1d2e',lineHeight:1.55}}>{u.note}</div>
                      </div>;
                    })}
                  </div>
                )}
                {detailTab==='comments'&&(
                  <div>
                    <div className="section-title">Parent Comments ({selected.parentComments?.length||0})</div>
                    {!(selected.parentComments?.length)&&<div style={{color:'#b0b5cc',fontSize:'.82rem'}}>No parent comments yet.</div>}
                    {(selected.parentComments||[]).map((cm,idx)=>(
                      <div key={idx} className="comment-card">
                        <div style={{fontSize:'.7rem',color:'#9094a8',marginBottom:5}}><strong>{cm.parent?.name||'Parent'}</strong> · {fmtDT(cm.date||cm.createdAt)}</div>
                        <div style={{fontSize:'.82rem',color:'#1a1d2e'}}>{cm.comment}</div>
                        {cm.reply&&<div className="reply-box"><strong>Teacher replied:</strong> {cm.reply}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Change Status Modal ── */}
      {modal==='status'&&createPortal(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal">
            <div className="m-hdr">
              <div className="m-hdr-l"><div className="m-icon"><ClipboardDocumentListIcon/></div><div><div className="m-title">Change ILP Status</div><div className="m-sub">{selected?.student?.user?.name}</div></div></div>
              <button className="m-close" onClick={()=>setModal(null)}><XMarkIcon/></button>
            </div>
            <div className="m-body">
              <div className="ff" style={{marginBottom:14}}>
                <label className="fl">New Status</label>
                <select className="fsel" style={{width:'100%'}} value={statusForm.status} onChange={e=>setStatusForm(p=>({...p,status:e.target.value}))}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="under_review">Under Review</option>
                  <option value="completed">Completed</option>
                  <option value="discontinued">Discontinued</option>
                </select>
              </div>
              {['completed','discontinued'].includes(statusForm.status)&&(
                <div className="ff">
                  <label className="fl">Closure Note</label>
                  <textarea className="fta" required value={statusForm.closureNote} onChange={e=>setStatusForm(p=>({...p,closureNote:e.target.value}))} placeholder="Reason for closing/discontinuing…"/>
                </div>
              )}
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={submitStatus} disabled={saving}>{saving?'Saving…':'Update Status'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Add Progress Modal ── */}
      {modal==='progress'&&createPortal(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal">
            <div className="m-hdr">
              <div className="m-hdr-l"><div className="m-icon"><ArrowPathIcon/></div><div><div className="m-title">Add Progress Update</div></div></div>
              <button className="m-close" onClick={()=>setModal(null)}><XMarkIcon/></button>
            </div>
            <div className="m-body">
              <div className="ff" style={{marginBottom:14}}>
                <label className="fl">Note *</label>
                <textarea className="fta" rows={4} value={progForm.note} onChange={e=>setProgForm(p=>({...p,note:e.target.value}))} placeholder="Progress observation…"/>
              </div>
              <div className="ff">
                <label className="fl">Overall Rating</label>
                <select className="fsel" style={{width:'100%'}} value={progForm.overallRating} onChange={e=>setProgForm(p=>({...p,overallRating:e.target.value}))}>
                  <option value="">— Select —</option>
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="satisfactory">Satisfactory</option>
                  <option value="needs_improvement">Needs Improvement</option>
                  <option value="concern">Concern</option>
                </select>
              </div>
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={submitProgress} disabled={saving}>{saving?'Saving…':'Add Update'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </>
  );
}